import React, { useEffect, useRef, useState } from 'react'
import styles from './SearchModal.module.sass'
import { EditorialeCard, RicercaBanner, RicercaProdottoCard } from '@components/molecules'
import useFindMenu from '@hooks/useFindMenu'
import Chip from '@components/atoms/Chip/Chip'
import { Search, WContainer } from '@components/atoms'
import { Close } from '@components/icons'
import { useRouter } from 'next/router'
import useTrans from '@hooks/useTrans'
import { handleSearchClickTracking } from '@utils/tracking'

interface Props {
  open?: boolean
  onClose?: () => void
  className?: string
}

const SearchModal = (props: Props) => {
  const { open = false, onClose = undefined, className = '' } = props
  const router = useRouter()
  const ref = useRef(null)
  const searchMenuItems = useFindMenu('search')
  const searchFrequenti = searchMenuItems?.children?.find(
    (child) => child.key === 'search-frequenti'
  )
  const menuItemBanner = useFindMenu('search-banner')
  const menuItemEditoriale = useFindMenu('search-editoriale')
  const [searchResults, setSearchResults] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [products, setProducts] = useState([])
  const t = useTrans()

  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        if (onClose) onClose()
      }
    }
    const focusHandler = (event) => {
      if (onClose && ref.current && !ref.current.contains(event.target)) onClose()
    }
    document.addEventListener('keydown', keyDownHandler)
    document.addEventListener('focusin', focusHandler)
    return () => {
      document.removeEventListener('keydown', keyDownHandler)
      document.removeEventListener('focusin', focusHandler)
    }
  }, [])

  useEffect(() => {
    if (open) document.body.classList.add('w-noscroll')
    else document.body.classList.remove('w-noscroll')
  }, [open])

  useEffect(() => {
    setSuggestions(searchResults?.autocomplete)
    setProducts(searchResults?.results)
  }, [searchResults])

  return open ? (
    <>
      <div className={styles.overlay} onClick={onClose}></div>
      <div className={`${styles.root} ${open ? styles['root--open'] : ''} ${className}`} ref={ref}>
        <WContainer>
          <div className={styles.titleContainer}>
            <h2 className={styles.title}>{t('Cosa stai cercando?')}</h2>
            <button
              type="button"
              aria-label={t('Chiudi')}
              className={styles.close}
              onClick={onClose}
            >
              <Close />
            </button>
          </div>
          <div className={styles.layout}>
            <div className={styles.leftLayout}>
              <Search
                ariaExpanded={suggestions?.length > 0}
                ariaControls="searchmodal_listbox"
                setSearchResults={setSearchResults}
                className={styles.search}
              />
              {!!(searchFrequenti?.children?.length || suggestions?.length) && (
                <h3
                  className={styles.subTitle}
                  id={suggestions?.length ? 'searchmodal_suggerimenti_label' : undefined}
                >
                  {suggestions?.length > 0 ? t('Ricerche suggerite') : t('Ricerche frequenti')}
                </h3>
              )}
              {suggestions?.length > 0 ? (
                <ul
                  className={styles.suggestions}
                  role="listbox"
                  id="searchmodal_listbox"
                  aria-labelledby="searchmodal_suggerimenti_label"
                >
                  {suggestions.map((suggestion, index) => (
                    <li key={index}>
                      <a
                        role="option"
                        href={`/${router.locale}/search/?q=${suggestion?.data?.autocomplete}`}
                        onClick={() =>
                          handleSearchClickTracking('', suggestion?.data?.autocomplete)
                        }
                        dangerouslySetInnerHTML={{ __html: suggestion?.highlight }}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <>
                  <div className={styles.frequentiList}>
                    {searchFrequenti?.children?.map((frequente) => (
                      <Chip
                        key={frequente.pk}
                        label={frequente.name}
                        href={`/${router.locale}/search?q=${frequente.name}`}
                      />
                    ))}
                  </div>
                  <RicercaBanner item={menuItemBanner} />
                </>
              )}
            </div>
            <div className={styles.rightLayout}>
              {products?.length > 0 ? (
                products.map((product) => {
                  return (
                    <div className={styles.productList}>
                      <RicercaProdottoCard
                        prodotto={{
                          pk: parseInt(product?.django_id),
                          nome: product?.titolo,
                          url: product?.url,
                          previewImage: product?.image,
                          categoria: {
                            alias: product?.categoria,
                            boxTitolo: product?.categoria,
                          },
                          utilizzo: product?.utilizzo_alias,
                          sottotitolo: product?.sottotitolo,
                          disponibile: product?.disponibile,
                        }}
                      />
                    </div>
                  )
                })
              ) : (
                <EditorialeCard
                  item={menuItemEditoriale}
                  orientation={'horizontal'}
                  className={styles.editorialCard}
                />
              )}
            </div>
          </div>
        </WContainer>
      </div>
    </>
  ) : (
    <></>
  )
}

export default SearchModal
